/*
  _________                   _____              _____
  __  ____/_____________________  /______ _________  /________
  _  /    _  __ _  __ \_  ___/  __/  __ `/_  __ \  __/_  ___/
  / /___  / /_/ /  / / /(__  )/ /_ / /_/ /_  / / / /_ _(__  )
  \____/  \____//_/ /_//____/ \__/ \__,_/ /_/ /_/\__/ /____/                               
*/

/* eslint-disable */

import get from '../../node_modules/lodash/get';

export const TINY_MCE_TEMPLATES = Object.freeze({
  AudioRecordingSuccess: (audioUrl) => {
    return `
      <span class="playposit-audio-upload">
        <audio style="width: 100%" controls controlslist="nodownload">
          <source src="${audioUrl}" type="audio/wav" />
        </audio>
      </span>
    `;
  },
  VideoRecordingSuccess: ({
    aspectPadding,
    url,
    playpositVideoId,
  } = {}) => {
    return `
    <div class="mux-video-wrapper" style="position: relative; height: 0; padding-bottom: ${aspectPadding}%">
      <iframe
        class="clickable"
        src="${url}hosted/video/?video_id=${playpositVideoId}"
        width="100%"
        height="100%"
        frameBorder="0"
        allowfullscreen
        style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;">
      </iframe>
    </div>
  `;
  }
});

export const TINY_MCE_CONFIG = Object.freeze({
  ConfigOptions: ({
      element,
      baseServerUrl,
      virtualNode,
      iOSSafari,
      service = 'legacy-dash',
    } = {}) => {
      const skinUrl =  `https://bunny.playposit.com/tinymce/skins/ui/${(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oxide-dark' : 'oxide')}`;
      const contentCss = `https://bunny.playposit.com/tinymce/skins/content/${(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'default')}/content.min.css`;
      const defaultMenuBarConfig = (service !== 'legacy-dash') ? 'edit insert format view' : '';
      const defaultToolbarConfig = iOSSafari
        ? `bold italic numlist showmenu`
        : `wami | undo redo | bold italic | alignleft aligncenter alignright alignjustify | numlist bullist`;
      return {
        custom: {
          node_api_prefix: baseServerUrl,
        },
        target: element,
        inline: virtualNode.inline,
        skin_url: skinUrl,
        content_style: `
          #tinymce > ul, #tinymce > ol {
            list-style-position: inside;
          }
          .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
            color: ${(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'white' : 'inherit')};
          }
          img.responsive {
            max-width: calc(100% - 10px);
            max-height: 500px;
            margin-left: 5px;
            height: auto;
          }
        `,
        content_css: contentCss,
        theme: 'silver',
        format: 'html',
        menubar: defaultMenuBarConfig,
        toolbar: defaultToolbarConfig,
        height: 300,
        min_width: 250,
        body_class: 'inline-textarea',
        table_default_styles: {
          width: '100%',
          'max-width': '100%',
        },
        resize: true,
        entity_encoding: 'raw',
        relative_urls: false,
        keep_styles: false,
        forced_root_block: virtualNode.inline ? '' : 'p',
        fixed_toolbar_container: `.fixed-toolbar-${virtualNode.tinymceKey}`,
        inline_boundaries_selector: 'a[href],span[data-pp-jump],input,span.playposit-audio-upload',
        link_assume_external_targets: true,
        plugins: [
          'autoresize eqneditor advlist autolink lists link image charmap',
          'searchreplace visualblocks code',
          'table paste autoresize imagetools',
        ],
        external_plugins: {
          eqneditor: `${baseServerUrl}hosted/eqneditor_v2/plugin.min.js`,
          charcount: `${baseServerUrl}hosted/charcount/plugin.js`,
        },
        style_formats: [
          {
            title: 'Headers',
            items: [
              {
                title: 'Header 1',
                format: 'h1',
              },
              {
                title: 'Header 2',
                format: 'h2',
              },
              {
                title: 'Header 3',
                format: 'h3',
              },
            ],
          },
          {
            title: 'Inline',
            items: [
              {
                title: 'Strikethrough',
                icon: 'strikethrough',
                format: 'strikethrough',
              },
              {
                title: 'Superscript',
                icon: 'superscript',
                format: 'superscript',
              },
              {
                title: 'Subscript',
                icon: 'subscript',
                format: 'subscript',
              },
              {
                title: 'Code',
                icon: 'code',
                format: 'code',
              },
            ],
          },
          {
            title: 'Alignment',
            items: [
              {
                title: 'Left',
                icon: 'alignleft',
                format: 'alignleft',
              },
              {
                title: 'Center',
                icon: 'aligncenter',
                format: 'aligncenter',
              },
              {
                title: 'Right',
                icon: 'alignright',
                format: 'alignright',
              },
              {
                title: 'Justify',
                icon: 'alignjustify',
                format: 'alignjustify',
              },
            ],
          },
        ],
        theme_advanced_font_sizes: '10px,12px,13px,14px,16px,18px,20px',
        font_size_style_values: '12px,13px,14px,16px,18px,20px',
        autosave_ask_before_unload: false,
        statusbar: false,
        images_upload_url: `${baseServerUrl}upload/image`,
        async images_upload_handler(blobInfo, success, failure) {
          try {
            if (virtualNode.readOnly) return failure('Images cannot be uploaded in read only mode.');
            await virtualNode.$timer(2000);
            const fd = new FormData();
            fd.append('file', blobInfo.blob(), blobInfo.filename());
            const { data: imageData } = await virtualNode.$tinymceApi.uploadImage(fd);
            return success(imageData.imageUrl);
          } catch(e) {
            return failure(e.message);
          }
        },
        image_class_list: [
          {
            title: 'Responsive',
            value: 'responsive',
          },
        ],
        setup: (editor) => {
          handleInlineEditorSetup(editor, virtualNode);
          handleCustomPluginSetup(editor, virtualNode);
          handleLoadingEditorInitialText(editor, virtualNode);
          handleConfiguringEditorEvents(editor, virtualNode);

          virtualNode.editor = editor;
          virtualNode.updateTinymceText(virtualNode.initialText);
          virtualNode.showLoadingScreen = false;
        },
      };
  },
});

// Helper Functions:
/**
 * Function that handles specfici TinyMCE inline editor init options.
 * 
 * @param {Object} editor 
 * @param {Object} virtualNode 
 */
function handleInlineEditorSetup(editor, virtualNode) {
  if (virtualNode.inline) {
    editor.ui.registry.addButton('showmenu', {
      title: 'Show menu',
      icon: 'more_vert',
      onAction() {
        const selector = `.fixed-toolbar-${virtualNode.tinymceKey} .mce-tinymce-inline.mce-container`;
        const mainContainer = document.querySelector(selector);
        if (!mainContainer) throw new Error(`Could not find "${selector}"`);
        mainContainer.classList[mainContainer.classList.contains('extended') ? 'remove' : 'add']('extended');
      },
    });
  }
}

/**
 * Function that handles configuring all our custom TinyMCE plugins.
 * 
 * @param {Object} editor 
 * @param {Object} virtualNode 
 */
function handleCustomPluginSetup(editor, virtualNode) {

  // Playlist doesn't support Video and Audio upload at the moment.
  if (virtualNode.service === 'playlist') return;

  // Audio Upload Plugin:
  if (
    !virtualNode.disabledButtons
    || !virtualNode.disabledButtons.includes('wami')
  ) {
    editor.ui.registry.addButton('wami', {
      title: 'Audio',
      text: '<img src="https://cdn.playposit.com/image-uploads/image-07-01-2021-14-03-02--mic.png"/>',
      onAction: function recordAudio() {
        virtualNode.recordMode = (virtualNode.recordMode === 'audio') ? null : 'audio';
        if (virtualNode.recordVode !== 'audio') return false;
        virtualNode.scrollToElement('.audio-recorder');
        return true;
      },
    });
  }

  // Video Upload Plugin:
  const hostingTeacherIdentity = get(virtualNode.rootTeacher, 'teacherIdentities', []).find((ti) => get(ti, 'identityType') === 'hosting');
  const hasHosting = (
    (
      _.has(virtualNode.rootTeacher, 'school.hosting')
      && virtualNode.rootTeacher.school.hosting === 1
    )
    || (
      get(virtualNode.rootTeacher, 'featureLists', []).length
      && parseInt(get(hostingTeacherIdentity, 'identityId', 0), 10)
    )
  );

  if (
    hasHosting
    && (
      get(virtualNode, 'localInteraction.interactionType') !== 'free_response'
      || get(virtualNode, 'localInteraction.config.enable_upload')
    )
  ) {
    editor.ui.registry.addButton('video', {
      title: 'Video',
      text: '<img src="https://cdn.playposit.com/image-uploads/image-07-01-2021-14-04-06--videocam.png"/>',
      onAction() {
        virtualNode.recordMode = (virtualNode.recordMode === 'video') ? null : 'video';
      },
    });
  }
}

/**
 * Honestly, function is self-explanitory :P
 * 
 * @param {Object} editor 
 * @param {Object} virtualNode 
 */
function handleLoadingEditorInitialText(editor, virtualNode) {
  if (virtualNode.initialText) {
    editor.on('LoadContent', () => {
      editor.setContent(virtualNode.initialText);
    });
  }
}

/**
 * Same with this one :P
 * @param {Object} editor 
 * @param {Object} virtualNode 
 */
function handleConfiguringEditorEvents(editor, virtualNode) {
  editor.on('change', (e) => virtualNode.watchEditor(e, editor));
  editor.on('keyUp', (e) => virtualNode.watchEditor(e, editor));
  editor.on('focus', (e) => {
    const toolbar = document.querySelector(`.fixed-toolbar-${virtualNode.tinymceKey}`);
    toolbar.classList.add('toolbar-open');
    virtualNode.isFocused = true;
    virtualNode.watchEditor(e, editor);
  });
  editor.on('blur', () => {
    virtualNode.isFocused = false;
    const toolbar = document?.querySelector(`.fixed-toolbar-${virtualNode.tinymceKey}`);
    toolbar?.classList?.remove('toolbar-open');
  }); 
}